// usePayoutBreakdown — count and amount per payout status over a period:
// the numbers behind every dashboard tile.
//
// Merchant-scoped, it asks the payouts LIST endpoint itself (limit=1, same
// window) — the tiles are then, by construction, the totals the list shows
// when a tile is clicked. Platform-wide (no merchant picked, admins only),
// it reads /v1/stats, whose breakdown is computed by the same backend
// function (payout.StatusBreakdown).
//
// The key starts ["payouts", merchantId] so anything that invalidates the
// payouts list (approve, cancel, repoll, upload) refreshes the tiles too.
import { useQuery } from "@tanstack/react-query";

import { listPayouts } from "@/api/payouts";
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useStats } from "@/queries/useStats";
import type { DateRangeBounds } from "@/utils/dateRange";

export interface PayoutBreakdown {
  /** status → count */
  counts: Record<string, number>;
  /** status → currency → paise */
  amounts: Record<string, Record<string, bigint>>;
  /** every payout in the period, any status */
  total: number;
}

export function usePayoutBreakdown(range: DateRangeBounds) {
  const status = useAuth((s) => s.status);
  const merchantId = useUI((s) => s.currentMerchantId);

  const scoped = useQuery({
    queryKey: ["payouts", merchantId, "breakdown", range.from ?? null, range.to ?? null],
    queryFn: async (): Promise<PayoutBreakdown> => {
      const page = await listPayouts(merchantId!, { from: range.from, to: range.to, limit: 1 });
      return {
        counts: page.statusCounts,
        amounts: page.statusAmountsMinor,
        total: Object.values(page.statusCounts).reduce((a, b) => a + b, 0),
      };
    },
    enabled: status === "authenticated" && merchantId != null,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  const stats = useStats(range);
  if (merchantId != null) {
    return { data: scoped.data, isLoading: scoped.isLoading, isError: scoped.isError };
  }
  const s = stats.data;
  return {
    data:
      s == null
        ? undefined
        : {
            counts: {
              needs_approval: s.payouts.needs_approval ?? 0,
              pending: s.payouts.pending,
              success: s.payouts.success,
              failure: s.payouts.failure,
            },
            amounts: s.amount_minor_by_status,
            total: s.payouts.total,
          },
    isLoading: stats.isLoading,
    isError: stats.isError,
  };
}
